export const translations = {
  en: {
    brandName: 'Systemise',
    nav: {
      problem: 'Problem',
      solution: 'Solution',
      caseStudies: 'Case Studies',
      testimonials: 'Testimonials',
      getStarted: 'Get Started'
    },
    hero: {
      badge: 'For Coaches & Consultants',
      title: 'Stop Drowning in Admin.',
      titleAccent: 'Start Growing Your Business.',
      description: 'We automate the repetitive tasks that steal your time—lead generation, sales, marketing, and fulfillment—so you can focus on what you do best: coaching and consulting.',
      ctaPrimary: 'Get Your Free Automation Audit',
      ctaSecondary: 'See How It Works',
      stats: {
        clients: 'Clients Served',
        hours: 'Hours Saved',
        growth: 'Avg. Growth'
      },
      floatingCard: {
        title: '80% Time Saved',
        subtitle: 'On admin tasks'
      }
    },
    problem: {
      title: 'The',
      titleAccent: 'Bottlenecks',
      titleEnd: 'Holding You Back',
      description: 'You became a coach or consultant to help people, not to spend your days trapped in repetitive admin work. Sound familiar?',
      items: [
        {
          title: 'Drowning in Admin Tasks',
          description: 'Spending more time on emails, scheduling, and follow-ups than actual coaching or consulting work',
          impact: 'Lost billable hours and client opportunities'
        },
        {
          title: 'Inconsistent Client Experience',
          description: 'Manual processes lead to missed follow-ups, delayed responses, and inconsistent service delivery',
          impact: 'Reduced client satisfaction and referrals'
        },
        {
          title: 'Limited Growth Capacity',
          description: 'Unable to scale beyond current client load due to time constraints and manual workflows',
          impact: 'Revenue plateau and burnout'
        },
        {
          title: 'No Time for Strategy',
          description: 'Constantly firefighting daily tasks instead of focusing on business growth and development',
          impact: 'Stagnant business development'
        }
      ],
      bottomText: 'These bottlenecks don\'t just waste time—they cap your income, drain your energy, and prevent you from reaching your full potential.',
      impactLabel: 'Impact:'
    },
    solution: {
      title: 'Create Space to',
      titleAccent: 'Breathe, Think & Grow',
      description: 'We implement intelligent automation systems that replace repetitive tasks with seamless workflows—giving you back your time and mental energy.',
      items: [
        {
          title: 'Lead Generation Automation',
          description: 'Capture, qualify, and nurture leads automatically while you sleep',
          features: ['Automated lead capture', 'Smart qualification', 'Personalized nurture sequences', 'CRM integration']
        },
        {
          title: 'Sales Process Optimization',
          description: 'Convert more prospects with automated follow-ups and seamless booking',
          features: ['Instant response systems', 'Smart scheduling', 'Payment automation', 'Proposal generation']
        },
        {
          title: 'Marketing Automation',
          description: 'Maintain consistent presence without constant manual effort',
          features: ['Email campaigns', 'Social media scheduling', 'Content distribution', 'Analytics tracking']
        },
        {
          title: 'Fulfillment & Delivery',
          description: 'Deliver consistent client experiences with automated workflows',
          features: ['Onboarding automation', 'Content delivery', 'Progress tracking', 'Feedback collection']
        }
      ],
      howItWorks: {
        title: 'How We Make It Happen',
        steps: [
          {
            title: 'Audit',
            description: 'We analyze your current workflows to identify time-wasting bottlenecks and automation opportunities.'
          },
          {
            title: 'Implement',
            description: 'We build and integrate custom automation systems tailored to your business needs and existing tools.'
          },
          {
            title: 'Optimize',
            description: 'We monitor, refine, and improve your systems to ensure maximum efficiency and ROI over time.'
          }
        ]
      }
    },
    caseStudies: {
      title: 'Real Results from',
      titleAccent: 'Real Businesses',
      description: 'See how coaches and consultants are transforming their businesses with our automation systems.',
      readMore: 'Read Full Case Study',
      labels: {
        challenge: 'Challenge',
        solution: 'Solution',
        results: 'Results'
      },
      items: [
        {
          clientName: 'Sarah Mitchell',
          business: 'Executive Life Coach',
          challenge: 'Spending 15+ hours weekly on lead follow-ups and scheduling',
          solution: 'Implemented automated lead nurturing system and smart calendar',
          results: '80% time saved on admin tasks, 3x client capacity increase'
        },
        {
          clientName: 'Marcus Johnson',
          business: 'Business Strategy Consultant',
          challenge: 'Manual invoicing and payment tracking consuming valuable time',
          solution: 'Automated billing, payment reminders, and financial reporting',
          results: '95% faster payment collection, zero missed invoices'
        },
        {
          clientName: 'Jennifer Park',
          business: 'Marketing Coach',
          challenge: 'Repetitive client onboarding and content delivery processes',
          solution: 'Automated onboarding workflows and content distribution system',
          results: '70% reduction in onboarding time, improved client experience'
        }
      ]
    },
    testimonials: {
      title: 'From administrative chaos',
      titleAccent: 'to growth',
      subtitle: 'Built for coaches & consultants who want to scale without chaos',
      description: 'See how entrepreneurs reclaim their time and drive more revenue with automation.',
      trustBadge: 'Client Satisfaction',
      stats: [
        { value: 'Up to 10+', label: 'hours saved per week' },
        { value: 'Faster', label: 'lead follow-up' },
        { value: 'More focus', label: 'on growth vs admin' },
        { value: 'Workflows', label: 'that free up time' }
      ],
      items: [
        {
          name: 'Rachel Thompson',
          role: 'Leadership Coach',
          company: 'Founder @ Thompson Coaching',
          rating: 5,
          quote: 'From drowning in admin to full focus on coaching. My revenue doubled in 3 months.'
        },
        {
          name: 'David Chen',
          role: 'Sales Consultant',
          company: 'Chen & Partners',
          rating: 4.5,
          quote: 'Lead response time from 24 hours to 5 minutes. Conversions up 40%. Finally time to grow.'
        },
        {
          name: 'Amanda Foster',
          role: 'Business Coach',
          company: 'Foster Strategy',
          rating: 5,
          quote: 'Strategic thinking instead of admin work. Clients are happier and so am I.'
        },
        {
          name: 'Michael Roberts',
          role: 'Consultant',
          company: 'Roberts Advisory',
          rating: 4.5,
          quote: 'Systems run 24/7. I can finally vacation without worrying about my business.'
        }
      ]
    },
    cta: {
      title: 'This is Your Next Step.',
      description: 'Answer a few short questions so we can assess how to automate your processes.',
      form: {
        contactTitle: 'Tell us who you are',
        nameLabel: 'Name',
        namePlaceholder: 'Your full name',
        emailLabel: 'Email',
        emailPlaceholder: 'you@email.com',
        phoneLabel: 'Phone number',
        phonePlaceholder: '+1 555 123 4567',
        phoneHint: 'Country code is detected automatically',
        next: 'Next',
        back: 'Back',
        step: 'Step',
        of: 'of',
        reviewTitle: 'Ready to submit?',
        reviewSubtitle: 'Review your details and submit your request.',
        submit: 'Submit Request',
        submitting: 'Submitting...',
        questions: [
          {
            label: 'What best describes your situation?',
            options: [
              'I just started as a coach/consultant',
              'I have clients but still work manually',
              'I already have systems but want to optimize',
              'My business is doing well and I want to automate & scale'
            ]
          },
          {
            label: 'What do you want to automate right now?',
            placeholder: 'Briefly describe which processes you want to automate (e.g. onboarding, sales, follow-up, content, etc.)\n\nNot sure? Just type \u201c-\u201d.'
          },
          {
            label: 'How do you manage this today?',
            options: [
              'Completely manual',
              'Partially with tools (e.g. Excel, Notion, Zapier, etc.)',
              'I already have an automated system'
            ]
          },
          {
            label: 'What is your biggest bottleneck right now?',
            options: [
              'Lack of time',
              'Too much manual work',
              'Leads don\'t convert well',
              'Chaos in systems/processes'
            ]
          },
          {
            label: 'How urgently do you want to solve this?',
            options: [
              'I\'m just gathering information',
              'Within 1\u20133 months',
              'As soon as possible'
            ]
          },
          {
            label: 'Are you willing to invest in a solution?',
            options: [
              'I want more info first',
              'Yes, if it saves me time and revenue',
              'Yes, I\'m ready to start'
            ]
          }
        ],
        thankYou: {
          high: {
            title: 'Great \u2014 this looks like a good match.',
            subtitle: 'Schedule a call so we can make this concrete.'
          },
          mid: {
            title: 'Good progress, {name}.',
            subtitle: 'We\'ll review your situation and get back to you with a proposal.',
            extra: 'Want to move faster? Schedule a call directly.'
          },
          low: {
            title: 'Thanks for your request, {name}.',
            subtitle: 'I\'ll review your situation and get back to you.',
            extra: 'Want to move faster? Schedule a call.'
          },
          button: 'Schedule Your Strategy Call'
        }
      },
      success: {
        title: 'Thank You!',
        message: 'Your request has been submitted. We\'ll be in touch within 24 hours.'
      }
    },
    footer: {
      description: 'Automating business processes for coaches and consultants.',
      quickLinks: 'Quick Links',
      services: 'Services',
      servicesList: [
        'Lead Generation',
        'Sales Automation',
        'Marketing Systems',
        'Fulfillment & Delivery'
      ],
      contact: 'Get in Touch',
      email: 'hello@systemise.be',
      legal: {
        rights: 'All rights reserved.',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        cookies: 'Cookie Policy'
      }
    }
  },
  nl: {
    brandName: 'Systemise',
    nav: {
      problem: 'Uitdagingen',
      solution: 'Aanpak',
      caseStudies: 'Casestudies',
      testimonials: 'Testimonials',
      getStarted: 'Aan de slag'
    },
    hero: {
      badge: 'Exclusief voor Coaches & Consultants',
      title: 'Wij Bouwen het Systeem.',
      titleAccent: 'Jij Plukt de Vruchten.',
      description: 'Coaches en consultants die met ons werken besparen 10+ uur per week en schalen hun omzet — zonder extra personeel. Wij automatiseren je leadgeneratie, sales, marketing en opvolging. Volledig op maat.',
      ctaPrimary: 'Plan je Strategiegesprek',
      ctaSecondary: 'Bekijk Resultaten',
      stats: {
        clients: 'Coaches geholpen',
        hours: 'Uur/week bespaard',
        growth: 'Gem. omzetgroei'
      },
      floatingCard: {
        title: 'Beperkt Aantal Plekken',
        subtitle: 'Max. 5 nieuwe klanten/maand'
      },
      trustLine: 'We werken bewust met een beperkt aantal klanten zodat we topkwaliteit kunnen leveren.'
    },
    problem: {
      title: 'De',
      titleAccent: 'Knelpunten',
      titleEnd: 'Die je Tegenhouden',
      description: 'Je bent coach of consultant geworden om mensen te helpen. Niet om je dagen te vullen met repetitieve administratie. Herkenbaar?',
      items: [
        {
          title: 'Verdrinken in Admin',
          description: 'E-mails beantwoorden, afspraken plannen, follow-ups sturen. Voordat je het weet is je dag voorbij — zonder een klant gecoacht te hebben.',
          impact: 'Verloren factureerbare uren'
        },
        {
          title: 'Inconsistente Klantervaring',
          description: 'Handmatig werk leidt tot gemiste follow-ups en trage reacties. Je klanten merken het verschil.',
          impact: 'Minder verwijzingen en herhaalaankopen'
        },
        {
          title: 'Groeiplafond Bereikt',
          description: 'Je wilt opschalen, maar je agenda zit vol. Meer klanten aannemen is simpelweg niet mogelijk zonder extra handen.',
          impact: 'Omzetplateau en burn-out'
        },
        {
          title: 'Geen Ruimte voor Strategie',
          description: 'Dagelijks brandjes blussen in plaats van nadenken over waar je naartoe wilt. De waan van de dag wint altijd.',
          impact: 'Stagnerende groei'
        }
      ],
      bottomText: 'Deze knelpunten verspillen niet alleen tijd. Ze beperken je inkomen, putten je energie uit en houden je weg van je volledige potentieel.',
      impactLabel: 'Impact:'
    },
    solution: {
      title: 'Creëer Ruimte om te',
      titleAccent: 'Ademen, Denken & Groeien',
      description: 'Wij bouwen slimme automatiseringen die repetitieve taken overnemen. Jij krijgt je tijd en mentale energie terug.',
      items: [
        {
          title: 'Leadgeneratie',
          description: 'Vang leads automatisch op, kwalificeer ze en koester ze. Ook terwijl je slaapt.',
          features: ['Automatische leadcapture', 'Slimme kwalificatie', 'Gepersonaliseerde opvolging', 'CRM-koppeling']
        },
        {
          title: 'Verkoop Optimalisatie',
          description: 'Meer klanten winnen dankzij geautomatiseerde follow-ups en moeiteloze planning.',
          features: ['Directe reactiesystemen', 'Slimme agenda-integratie', 'Betalingsautomatisering', 'Offerte-generatie']
        },
        {
          title: 'Marketing op Autopilot',
          description: 'Blijf zichtbaar zonder er dagelijks mee bezig te zijn. Consistent en professioneel.',
          features: ['E-mailcampagnes', 'Social media planning', 'Content distributie', 'Resultaten meten']
        },
        {
          title: 'Levering & Onboarding',
          description: 'Geef elke klant dezelfde topervaring. Automatisch, van intake tot afronding.',
          features: ['Onboarding flows', 'Content levering', 'Voortgang bijhouden', 'Feedback verzamelen']
        }
      ],
      howItWorks: {
        title: 'Hoe We Het Aanpakken',
        steps: [
          {
            title: 'Audit',
            description: 'We brengen je huidige workflows in kaart. Waar verlies je tijd? Waar liggen de kansen?'
          },
          {
            title: 'Implementatie',
            description: 'We bouwen automatiseringen op maat. Afgestemd op jouw bedrijf en bestaande tools.'
          },
          {
            title: 'Optimalisatie',
            description: 'We blijven monitoren en verbeteren. Zodat je systemen steeds efficiënter worden.'
          }
        ]
      }
    },
    caseStudies: {
      title: 'Echte Resultaten van',
      titleAccent: 'Echte Bedrijven',
      description: 'Ontdek hoe coaches en consultants hun bedrijf transformeren met onze automatiseringen.',
      readMore: 'Lees Volledige Case',
      labels: {
        challenge: 'Uitdaging',
        solution: 'Aanpak',
        results: 'Resultaat'
      },
      items: [
        {
          clientName: 'Sarah Mitchell',
          business: 'Executive Life Coach',
          challenge: 'Wekelijks 15+ uur kwijt aan lead follow-ups en planning.',
          solution: 'Geautomatiseerd lead nurturing systeem en slimme agenda opgezet.',
          results: '80% minder tijd aan admin. 3x meer klantcapaciteit.'
        },
        {
          clientName: 'Marcus Johnson',
          business: 'Bedrijfsstrategie Consultant',
          challenge: 'Facturering en betalingen kosten te veel tijd en aandacht.',
          solution: 'Volledige automatisering van facturatie, herinneringen en rapportage.',
          results: '95% snellere betalingen. Nul gemiste facturen.'
        },
        {
          clientName: 'Jennifer Park',
          business: 'Marketing Coach',
          challenge: 'Onboarding en content delivery is steeds hetzelfde handwerk.',
          solution: 'Geautomatiseerde onboarding flow en content distributie opgezet.',
          results: '70% minder onboarding-tijd. Betere klantervaring.'
        }
      ]
    },
    testimonials: {
      title: 'Van administratieve chaos',
      titleAccent: 'naar groei',
      subtitle: 'Voor coaches & consultants die willen opschalen. Zonder chaos.',
      description: 'Ontdek hoe ondernemers tijd terugwinnen en meer omzet draaien.',
      trustBadge: 'Klanttevredenheid',
      stats: [
        { value: 'Tot 10+ uur', label: 'per week bespaard' },
        { value: 'Snellere', label: 'opvolging van leads' },
        { value: 'Meer focus', label: 'op groei i.p.v. admin' },
        { value: 'Workflows', label: 'die tijd vrijmaken' }
      ],
      items: [
        {
          name: 'Rachel Thompson',
          role: 'Leadership Coach',
          company: 'Founder @ Thompson Coaching',
          rating: 5,
          quote: 'Van verdronken in admin naar volle focus op coaching. Mijn omzet verdubbelde in 3 maanden.'
        },
        {
          name: 'David Chen',
          role: 'Sales Consultant',
          company: 'Chen & Partners',
          rating: 4.5,
          quote: 'Reactietijd op leads: van 24 uur naar 5 minuten. Conversies +40%. Eindelijk ruimte om te groeien.'
        },
        {
          name: 'Amanda Foster',
          role: 'Business Coach',
          company: 'Foster Strategy',
          rating: 5,
          quote: 'Strategisch denken in plaats van admin doen. Klanten zijn blijer en ik ook.'
        },
        {
          name: 'Michael Roberts',
          role: 'Consultant',
          company: 'Roberts Advisory',
          rating: 4.5,
          quote: 'Mijn systemen draaien 24/7. Ik kan op vakantie zonder zorgen.'
        }
      ]
    },
    cta: {
      title: 'Dit is jouw volgende Stap.',
      description: 'Beantwoord een paar korte vragen zodat we kunnen inschatten hoe we jouw processen kunnen automatiseren.',
      form: {
        contactTitle: 'Vertel ons wie je bent',
        nameLabel: 'Naam',
        namePlaceholder: 'Je volledige naam',
        emailLabel: 'E-mail',
        emailPlaceholder: 'je@email.com',
        phoneLabel: 'GSM-nummer',
        phonePlaceholder: '0472 12 34 56',
        phoneHint: 'Landcode wordt automatisch herkend',
        next: 'Volgende',
        back: 'Terug',
        step: 'Stap',
        of: 'van',
        reviewTitle: 'Klaar om te versturen?',
        reviewSubtitle: 'Controleer je gegevens en verstuur je aanvraag.',
        submit: 'Verstuur Aanvraag',
        submitting: 'Versturen...',
        questions: [
          {
            label: 'Wat beschrijft jouw situatie het best?',
            options: [
              'Ik ben net gestart als coach/consultant',
              'Ik heb klanten maar werk nog manueel',
              'Ik heb al systemen maar wil optimaliseren',
              'Mijn business draait goed en ik wil automatiseren & schalen'
            ]
          },
          {
            label: 'Wat wil je op dit moment automatiseren?',
            placeholder: 'Beschrijf kort welke processen je wil automatiseren (bv. onboarding, sales, opvolging, content, etc.)'
          },
          {
            label: 'Hoe beheer je dit vandaag?',
            options: [
              'Volledig manueel',
              'Gedeeltelijk met tools (bv. Excel, Notion, Zapier, etc.)',
              'Ik heb al een geautomatiseerd systeem'
            ]
          },
          {
            label: 'Wat is je grootste bottleneck momenteel?',
            options: [
              'Tijdgebrek',
              'Te veel manueel werk',
              'Leads converteren niet goed',
              'Chaos in systemen/processen'
            ]
          },
          {
            label: 'Hoe dringend wil je dit oplossen?',
            options: [
              'Ik ben gewoon info aan het verzamelen',
              'Binnen 1\u20133 maanden',
              'Zo snel mogelijk'
            ]
          },
          {
            label: 'Ben je bereid te investeren in een oplossing?',
            options: [
              'Ik wil eerst meer info',
              'Ja, als het mij tijd en omzet oplevert',
              'Ja, ik ben klaar om te starten'
            ]
          }
        ],
        thankYou: {
          high: {
            title: 'Top \u2014 dit lijkt een goede match.',
            subtitle: 'Plan een gesprek zodat we dit concreet kunnen maken.'
          },
          mid: {
            title: 'Goed bezig, {name}.',
            subtitle: 'We bekijken je situatie en nemen snel contact op met een voorstel.',
            extra: 'Wil je sneller schakelen? Plan direct een gesprek.'
          },
          low: {
            title: 'Bedankt voor je aanvraag, {name}.',
            subtitle: 'Ik bekijk je situatie en kom hierop terug.',
            extra: 'Wil je sneller vooruit? Plan een gesprek.'
          },
          button: 'Plan je Strategiegesprek'
        }
      },
      success: {
        title: 'Dank Je!',
        message: 'Je aanvraag is ingediend. We nemen binnen 24 uur contact met je op.'
      }
    },
    footer: {
      description: 'Slimme automatisering voor coaches en consultants.',
      quickLinks: 'Snelle Links',
      services: 'Diensten',
      servicesList: [
        'Leadgeneratie',
        'Verkoop',
        'Marketing',
        'Levering & Onboarding'
      ],
      contact: 'Neem Contact Op',
      email: 'rozario@systemise.be',
      legal: {
        rights: 'Alle rechten voorbehouden.',
        privacy: 'Privacybeleid',
        terms: 'Servicevoorwaarden',
        cookies: 'Cookiebeleid'
      }
    }
  }
};